import type { AgentEdge, AgentNode } from '@saaga/shared/interfaces/agency-node.interface'
import type { AgentTool } from '@saaga/shared/interfaces/agent-tool.interface'
import type { LlmModel } from '@saaga/shared/interfaces/llm-model.interface'
import { ReactFlowProvider } from '@xyflow/react'
import { type ReactNode, useRef } from 'react'

import { cn } from '@/lib/utils'

import '@xyflow/react/dist/style.css'
import './agency-flow-canvas.css'

import { CANVAS_BACKGROUND_STYLE } from './agency-flow-canvas.config'
import { AgencyFlowDialogs } from './agency-flow-dialogs.atom'
import { AgencyFlowReactFlow } from './agency-flow-react-flow.atom'
import { useAgencyFlowCanvas } from './use-agency-flow-canvas.hook'

export interface AgencyFlowCanvasProps {
  initialNodeList?: AgentNode[]
  initialEdgeList?: AgentEdge[]
  onChange?: (nodeList: AgentNode[], edgeList: AgentEdge[]) => void
  className?: string
  modelList?: LlmModel[]
  isLoadingModels?: boolean
  toolList?: AgentTool[]
  isLoadingTools?: boolean
  onExport: () => void
  onImport: (file: File) => void
}

const AgencyFlowCanvasInner = ({
  initialNodeList,
  initialEdgeList,
  onChange,
  className,
  modelList = [],
  isLoadingModels = false,
  toolList = [],
  isLoadingTools = false,
  onExport,
  onImport,
}: AgencyFlowCanvasProps): ReactNode => {
  const reactFlowWrapper = useRef<HTMLDivElement>(null)
  const canvas = useAgencyFlowCanvas(initialNodeList, initialEdgeList, onChange)

  return (
    <div
      ref={reactFlowWrapper}
      className={cn('agency-flow-canvas h-full w-full', className)}
      style={CANVAS_BACKGROUND_STYLE}
    >
      <AgencyFlowReactFlow canvas={canvas} onExport={onExport} onImport={onImport} />
      <AgencyFlowDialogs
        canvas={canvas}
        nodeData={canvas.agencyFlow.selectedNode?.data ?? null}
        nodeId={canvas.agencyFlow.selectedNodeId}
        modelList={modelList}
        isLoadingModels={isLoadingModels}
        toolList={toolList}
        isLoadingTools={isLoadingTools}
      />
    </div>
  )
}

export const AgencyFlowCanvas = (props: AgencyFlowCanvasProps): ReactNode => (
  <ReactFlowProvider>
    <AgencyFlowCanvasInner {...props} />
  </ReactFlowProvider>
)

AgencyFlowCanvas.displayName = 'AgencyFlowCanvas'
